import React, { useEffect, useRef, useState } from 'react';
import io from 'socket.io-client';
import { FaMicrophone, FaMicrophoneSlash, FaVolumeUp, FaVolumeMute } from 'react-icons/fa';

const VoiceChat = ({ roomId, userId }) => {
  const [isMuted, setIsMuted] = useState(false);
  const [isAudioDisabled, setIsAudioDisabled] = useState(false);
  const socketRef = useRef(null);
  const peerConnections = useRef({});
  const localStreamRef = useRef(null);
  const remoteAudios = useRef({});

  const createPeerConnection = (otherUserId) => {
    if (peerConnections.current[otherUserId]) {
      return peerConnections.current[otherUserId];
    }

    const config = {
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        {
          urls: [
            'turn:relay.metered.ca:443',
            'turns:relay.metered.ca:443?transport=tcp'
          ],
          username: 'open',
          credential: 'open'
        }
      ],
      iceTransportPolicy: 'all'
    };

    const peer = new RTCPeerConnection(config);

    peer.onicecandidate = (event) => {
      if (event.candidate && socketRef.current?.connected) {
        socketRef.current.emit('ice-candidate', {
          to: otherUserId,
          candidate: event.candidate,
          roomId
        });
      }
    };

    peer.oniceconnectionstatechange = () => {
      if (['disconnected', 'failed'].includes(peer.iceConnectionState)) {
        cleanUpUser(otherUserId);
      }
    };

    peer.ontrack = (event) => {
      if (event.streams.length > 0 && !remoteAudios.current[otherUserId]) {
        const audio = new Audio();
        audio.srcObject = event.streams[0];
        audio.autoplay = true;
        audio.muted = isAudioDisabled;
        remoteAudios.current[otherUserId] = audio;

        audio.play().catch(() => {
          const handleUserInteraction = () => {
            audio.play().finally(() => {
              document.removeEventListener('click', handleUserInteraction);
            });
          };
          document.addEventListener('click', handleUserInteraction);
        });
      }
    };

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        if (track.kind === 'audio') {
          peer.addTrack(track, localStreamRef.current);
        }
      });
    }

    peerConnections.current[otherUserId] = peer;
    return peer;
  };

  // Clean up when a user leaves
  const cleanUpUser = (userId) => {
    if (peerConnections.current[userId]) {
      peerConnections.current[userId].close();
      delete peerConnections.current[userId];
    }
    
    if (remoteAudios.current[userId]) {
      remoteAudios.current[userId].pause();
      remoteAudios.current[userId].srcObject = null;
      delete remoteAudios.current[userId];
    }
  };

  const initiateConnection = async (otherUserId) => {
    try {
      const peer = createPeerConnection(otherUserId);
      const offer = await peer.createOffer({ offerToReceiveAudio: 1 });
      await peer.setLocalDescription(offer);
      
      socketRef.current.emit('offer', {
        to: otherUserId,
        sdp: offer,
        from: userId,
        roomId
      });
    } catch (err) {
      setTimeout(() => initiateConnection(otherUserId), 2000);
    }
  };

  const handleOffer = async (from, sdp) => {
    try {
      const peer = createPeerConnection(from);
      await peer.setRemoteDescription(new RTCSessionDescription(sdp));
      const answer = await peer.createAnswer({ offerToReceiveAudio: 1 });
      await peer.setLocalDescription(answer);

      socketRef.current.emit('answer', {
        to: from,
        sdp: answer,
        from: userId,
        roomId
      });
    } catch (err) {
      console.error('Offer handling failed:', err);
    }
  };

  const handleAnswer = async (from, sdp) => {
    try {
      const peer = peerConnections.current[from];
      if (peer) {
        await peer.setRemoteDescription(new RTCSessionDescription(sdp));
      }
    } catch (err) {
      console.error('Answer handling failed:', err);
    }
  };

  const handleIceCandidate = async (from, candidate) => {
    try {
      const peer = peerConnections.current[from];
      if (peer && candidate) {
        await peer.addIceCandidate(new RTCIceCandidate(candidate));
      }
    } catch (err) {
      console.error('ICE candidate handling failed:', err);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          },
          video: false
        });

        localStreamRef.current = stream;
        setIsMuted(false);

        const socket = io(process.env.REACT_APP_BASE_PATH, {
          path: '/socket.io',
          transports: ['polling'],
          upgrade: false,
          reconnectionAttempts: 5,
          reconnectionDelay: 1000,
          timeout: 20000,
          query: { 
            userId,
            roomId,
            clientType: 'webrtc'
          }
        });

        socketRef.current = socket;

        socket.on('connect', () => {
          socket.emit('join-room', roomId, userId);
        });

        socket.on('user-joined', (joinedUserId) => {
          if (joinedUserId !== userId && !peerConnections.current[joinedUserId]) {
            setTimeout(() => initiateConnection(joinedUserId), 1000);
          }
        });

        socket.on('user-left', (leftUserId) => {
          if (leftUserId !== userId) {
            cleanUpUser(leftUserId);
          }
        });

        socket.on('offer', ({ from, sdp }) => handleOffer(from, sdp));
        socket.on('answer', ({ from, sdp }) => handleAnswer(from, sdp));
        socket.on('ice-candidate', ({ from, candidate }) => handleIceCandidate(from, candidate));

      } catch (err) {
        console.error('Initialization failed:', err);
      }
    };

    initialize();

    return () => {
      if (socketRef.current) {
        socketRef.current.emit('leave-room', roomId, userId);
        socketRef.current.disconnect();
      }

      Object.values(peerConnections.current).forEach(pc => pc?.close());
      Object.values(remoteAudios.current).forEach(audio => {
        if (audio) {
          audio.pause();
          audio.srcObject = null;
        }
      });

      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [roomId, userId]);

  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        const newMuted = !audioTrack.enabled;
        audioTrack.enabled = newMuted;
        setIsMuted(!newMuted);
      }
    }
  };

  const toggleAudioOutput = () => {
    const newState = !isAudioDisabled;
    setIsAudioDisabled(newState);
    Object.values(remoteAudios.current).forEach(audio => {
      if (audio) audio.muted = newState;
    });
  };

  window.controlVoiceFeature = async function(type) {
    if (type === "mic") toggleMute();
    else if (type === "audio") toggleAudioOutput();
  };

  return (
    <>
   <div className="position-absolute p-3 bottom-0 end-0">
      <div className="d-flex gap-2 justify-between items-center mb-6">
        <button
          onClick={toggleAudioOutput}
          className={`px-4 py-2 rounded-full font-semibold flex items-center text-white bg-dark border-0 rounded-5`}
        >
          {isAudioDisabled ? <FaVolumeMute className="mr-2" /> : <FaVolumeUp className="mr-2" />}
        </button>
        <button
          onClick={toggleMute}
          disabled={!localStreamRef.current}
          className={`px-4 py-2 rounded-full font-semibold flex items-center text-white bg-dark border-0 rounded-5`}
        >
          {isMuted ? <FaMicrophoneSlash className="mr-2" /> : <FaMicrophone className="mr-2" />}
        </button>
      </div>
    </div>
    </>
  );
};

export default VoiceChat;